import cn from 'classnames';
import { isEmpty } from 'ramda';
import { FC } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { FeatureFlag } from '@/shared/api/protocol-ts/model/dto_organization_pb';
import {
  ReportPrintSettings,
  ReportType,
} from '@/shared/api/protocol-ts/model/dto_report_pb';
import { RenderPreviewSettings } from '@/shared/config';
import { useAppSelector } from '@/shared/hooks';

import { conditionModel } from '@/entities/condition';
import { organizationModel } from '@/entities/organization';
import { useFormatPatientDateOfBirth } from '@/entities/patient';
import { getReportSignStatus, i18n } from '@/entities/reports';
import { ToothChartLegends, toothModel } from '@/entities/tooth';

import { ExtendedConclusion } from '@/features/extendedConclusion';
import {
  useFDA3DGPRestrictions,
  useFDAEnvironment,
} from '@/features/fdaRestrictions';

import { PreviewConclusion } from '@/widgets/Conclusion';
import { IOXRayReportRender } from '@/widgets/IOXRayReportRender';
import { RarePathologies } from '@/widgets/NonDentalFindings';
import { PanowingsReportRender } from '@/widgets/PanowingsReportRender';

import { PanoReformatRender } from '@/pages/GpReport/ui/PanoReformatRender/PanoReformatRender';
import { PanoReportRender } from '@/pages/PanoReport/ui/PanoReportRender/PanoReportRender';

import { previewFooterDisclaimer, UsePreviewDocument } from '../../config';
import { FooterDate } from '../FooterDate/FooterDate';
import { PreviewFooter } from '../PreviewFooter/PreviewFooter';
import { PreviewHeader } from '../PreviewHeader/PreviewHeader';
import { PreviewScanInfo } from '../PreviewScanInfo/PreviewScanInfo';
import { PreviewToothCard } from '../PreviewToothCard/PreviewToothCard';
import { PreviewToothChart } from '../PreviewToothChart/PreviewToothChart';
import { ReportInfo } from '../ReportInfo/ReportInfo';
import { Signature } from '../Signature/Signature';

import styles from './PreviewDocument.module.scss';

type PreviewDocumentType = {
  documentData: UsePreviewDocument;
  settings: ReportPrintSettings;
  showLowProbability: boolean;
  organizationID: string;
  userTimezone: string;
};

export const PreviewDocument: FC<PreviewDocumentType> = (props) => {
  const {
    documentData,
    settings: {
      IsInverted,
      BlackAndWhiteMode,
      ShowSlices,
      ShowDentalChart,
      ShowDiagnosesProbabilities,
      ShowDiagnoses,
      ShowBBoxes,
      ShowStudyMetadata,
      ShowMasks,
    },
    organizationID,
    userTimezone,
  } = props;

  const maxFaxConditionsGrouped = useAppSelector(
    conditionModel.selectors.selectMaxFaxGroupedByCode,
  );

  const conditionLoading = useAppSelector(
    conditionModel.selectors.selectLoading,
  );

  const toothLoading = useAppSelector(toothModel.selectors.selectLoading);
  const showToothChart =
    conditionLoading === 'succeeded' && toothLoading === 'succeeded';

  const { formatMessage, formatDate } = useIntl();

  const {
    teeth = [],
    patient,
    report,
    CBCTPanoImage,
    patientFullName,
    studyMeta,
    isLoading,
    isFailed,
    isDICOM,
    studyDate,
  } = documentData;

  const organization = useAppSelector((state) =>
    organizationModel.selectors.organizationSelectors.selectById(
      state,
      organizationID,
    ),
  );

  const hideNonDentalFindings = useAppSelector(
    organizationModel.selectors.selectFeatureFlag(
      FeatureFlag.FeatureFlag_Hide_NonDentalFindings,
    ),
  );

  const isExtendedConclusionEnabled = useAppSelector(
    organizationModel.selectors.selectFeatureFlag(
      FeatureFlag.FeatureFlag_Show_CBCTExtendedConclusion,
    ),
  );

  const { shouldHideOtherFindings, shouldApplyToothChartRestrictions } =
    useFDA3DGPRestrictions();

  const { isFDA, isFDAVersion2, masksVisible } = useFDAEnvironment();

  const showExtendedConclusion =
    report?.Type === ReportType.ReportType_CBCT_GP &&
    isExtendedConclusionEnabled;

  const patientDateOfBirth = patient?.PersonalData?.DateOfBirth;

  const patientDoB = useFormatPatientDateOfBirth({ patientDateOfBirth });

  const isReportSigned = report?.Signature?.Signed;

  const allUserSignatures = report?.Signature?.UserSignatures;

  if (isLoading || isFailed) {
    return null;
  }

  // NOTE: A failed stream releases the loading gate without data, so this guard
  // keeps the PDF pipeline from printing a report-shaped page with an epoch date.
  if (!report || !patient) {
    return null;
  }

  // Get view data after patient and report data is fetched
  const reportFormattedDate = formatDate(report?.Created?.At?.toDate() ?? 0, {
    dateStyle: 'medium',
    timeStyle: 'short',
    timeZone: userTimezone,
  });

  const studyFormattedDate = studyDate
    ? formatDate(studyDate, {
        dateStyle: 'medium',
        timeStyle: 'short',
        timeZone: userTimezone,
      })
    : '';

  const reportTitle = report?.Type ? formatMessage(i18n[report.Type]) : '';
  const reportSignStatus = getReportSignStatus(report);

  const descriptionLabel = isReportSigned
    ? formatMessage({
        id: 'previewDocument.reportSigned',
        defaultMessage: 'Report signed:',
      })
    : formatMessage({
        id: 'previewDocument.reportCreated',
        defaultMessage: 'Report created:',
      });

  const previewSettings: RenderPreviewSettings = {
    isPreview: true,
    isInverted: IsInverted,
    isBW: BlackAndWhiteMode,
    // FDA unaided (IOXRAY/PANO AI) reports must never show masks on the preview
    showMasks: isFDA && !masksVisible ? false : ShowMasks,
    showPanoramaSplit: report?.Settings?.ShowPanoramaSplitInsteadOfSingle,
  };

  return (
    <table className={styles.table} data-component="preview-document">
      <tbody>
        <tr>
          <td>
            <div className={styles.document}>
              <PreviewHeader
                blackAndWhiteMode={BlackAndWhiteMode}
                organization={organization}
              />

              {showExtendedConclusion && (
                <div
                  className={cn(
                    styles.extendedConclusionContainer,
                    styles.pageBreakAfter,
                  )}
                >
                  <h1 className="h4">{reportTitle}</h1>

                  <ExtendedConclusion isPreview={true} />
                </div>
              )}

              <div className={styles.panoContainer}>
                {!isEmpty(CBCTPanoImage) && (
                  <PanoReformatRender
                    previewSettings={previewSettings}
                    className={styles.panoBackgroundColor}
                  />
                )}

                {report?.Type === ReportType.ReportType_IOXRay_GP && (
                  <IOXRayReportRender
                    previewSettings={previewSettings}
                    className={styles.panoBackgroundColor}
                  />
                )}

                {report?.Type === ReportType.ReportType_Pano_Bitewings && (
                  <PanowingsReportRender
                    className={cn(
                      styles.IOXRayMatrixWrapper,
                      styles.panoBackgroundColor,
                    )}
                    previewSettings={previewSettings}
                  />
                )}

                {report?.Type === ReportType.ReportType_Pano_GP && (
                  <PanoReportRender
                    previewSettings={previewSettings}
                    className={styles.panoBackgroundColor}
                  />
                )}

                <ReportInfo
                  reportTitle={reportTitle}
                  patientFullName={patientFullName}
                  patientDoB={patientDoB}
                  studyFormattedDate={studyFormattedDate}
                />
              </div>

              {ShowDentalChart &&
                report?.Type !== ReportType.ReportType_CBCT_Endo && (
                  <>
                    {showToothChart && <PreviewToothChart />}

                    <ToothChartLegends
                      blackAndWhiteMode={BlackAndWhiteMode}
                      isFDAMode={shouldApplyToothChartRestrictions}
                      showTreatedLegend={isFDAVersion2}
                    />
                  </>
                )}

              {!hideNonDentalFindings &&
                !shouldHideOtherFindings &&
                ShowBBoxes &&
                Object.keys(maxFaxConditionsGrouped).length > 0 && (
                  <div className={styles.dontBreakInside}>
                    <h5 className="h5">
                      <FormattedMessage
                        id="report.rarePathology.header"
                        defaultMessage="Other findings"
                      />
                    </h5>

                    {report && (
                      <RarePathologies
                        className={styles.rarePathologies}
                        conditionsGrouped={maxFaxConditionsGrouped}
                        isPreviewMode={true}
                      />
                    )}
                  </div>
                )}

              {!!teeth.length && (
                <div className={styles.teethContainer}>
                  {teeth.map((tooth) => (
                    <PreviewToothCard
                      key={tooth.ID}
                      tooth={tooth}
                      showProbability={ShowDiagnosesProbabilities}
                      showConditions={ShowDiagnoses}
                      showMedicalImages={ShowSlices}
                      inverted={IsInverted}
                      blackAndWhiteMode={BlackAndWhiteMode}
                      report={report}
                      isDICOM={isDICOM}
                    />
                  ))}
                </div>
              )}

              {!!report?.LegacyConclusion && report?.Signature?.Signed && (
                <PreviewConclusion className={styles.conclusion} />
              )}

              <div
                className={cn(styles.signatureAndDate, {
                  [styles.blackSignatureAndDate]: BlackAndWhiteMode,
                })}
              >
                {isReportSigned ? (
                  allUserSignatures &&
                  allUserSignatures.map((signature) => (
                    <Signature
                      signature={signature}
                      key={signature.UserID}
                      blackAndWhiteMode={BlackAndWhiteMode}
                      descriptionLabel={descriptionLabel}
                      userTimezone={userTimezone}
                    />
                  ))
                ) : (
                  <FooterDate
                    date={reportFormattedDate}
                    label={descriptionLabel}
                  />
                )}
              </div>

              {ShowStudyMetadata && (
                <PreviewScanInfo
                  className={styles.scanInfo}
                  studyMeta={studyMeta}
                />
              )}

              <PreviewFooter
                text={formatMessage(previewFooterDisclaimer[reportSignStatus])}
                blackAndWhiteMode={BlackAndWhiteMode}
              />
            </div>
          </td>
        </tr>
      </tbody>
      <tfoot>
        <tr>
          <td>
            <div className={styles.footerSpace}>&nbsp;</div>
          </td>
        </tr>
      </tfoot>
    </table>
  );
};
